import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Button, CircularProgress, Divider, IconButton, Modal, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import { customScrollbarStyle } from '../../../../assets/styles/custom.styles';
import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import { useAuth } from '../../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import { IntervalsObject } from '../../../../interfaces/meeting';
import { setCanAddInterval } from '../../../../slices/meetings';
import { RootState } from '../../../../utils/store';
import SelectDurationDropdown from '../common/editAvailability/selectDuration';
import IntervalComponent from '../common/editAvailability/setIntervalComponent';
import { convertTimeRangeHourFormat, formatDate, timeToMinutes } from '../common/helpers';


interface EditAvailabilityModalProps {
  open: boolean;
  setClose: () => void;
  date: string;
  refresh: () => void;
}

export interface ScheduleDataObjectFromAPI {
  id: string;
  date_start: string;
  date_end: string;
  time_slots: IntervalsObject[],
  weekdays: number[],
  duration: string,
  duration_type: number,
  tenant: number,
  type: number
}

export interface ScheduleDataObject {
  id: string;
  dateStart: string;
  dateEnd: string;
  timeSlots: IntervalsObject[];
  weekdays?: number[];
  duration: number | null;
  durationType: number | null;
  tenant: number | null;
  type: number | null;
}

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 480,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    p: 4,
  },
  headerStyle: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '18px',
  },
  bodyTextStyle: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 400,
    fontSize: '16px',
  },
  customTextfieldStyle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: '0em',
    textAlign: 'left',
    color: '#878787',
    padding:'8px 8px',
    borderRadius: '6px',
    backgroundColor: '#00000008',
    
  },
  customTextfieldStyleHasValue: {
    bgcolor: 'transparent',
    border:'1px solid rgba(0, 0, 0, 0.50)',
  },
  buttonStyle: {
    textTransform: 'none',
    bgcolor: '#2449FC',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '16px',
    '&:hover': {
      bgcolor: '#FFFFFF',
      color: '#2449FC',
      border: '1px solid #2449FC',
    },
  },
  buttonStyleCancel: {
    textTransform: 'none',
    bgcolor: 'transparent',
    borderRadius: '50px',
    color: '#878787',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '16px',
    border: '1px solid #878787',
    '&:hover': {
      bgcolor: '#FFFFFF',
      color: '#2449FC',
      border: '1px solid #878787',
    },
  },
  buttonTextOnly: {
    bgcolor: 'transparent',
    color: '#F83B3B',
    fontWeight: 500,
    '&:hover': {
      bgcolor: 'transparent',
      color: '#F83B3B',
      border: 'none',
    },
  },
  buttonTextAdd: {
    bgcolor: 'transparent',
    color: '#00CECF',
    width: '150px',
    fontWeight: 500,
    '&:hover': {
      bgcolor: 'transparent',
      color: '#00CECF',
      border: 'none',
    },
  },
  unavailable: {
    border: '1px solid #E3E3E3',
    width: '100%',
    padding: '16px 8px',
    borderRadius: '6px',
  },
  intervalContaner: {
    maxHeight: '200px',
    ...customScrollbarStyle,
  },
};

const transformDataFromAPI = (dataFromAPI: ScheduleDataObjectFromAPI[] ):ScheduleDataObject  => {
  if (dataFromAPI) {
    const scheduleData = dataFromAPI[0];
    return ({
      id: scheduleData?.id,
      dateStart: scheduleData?.date_start,
      dateEnd: scheduleData?.date_end,
      timeSlots: convertTimeRangeHourFormat(scheduleData?.time_slots, true),
      duration: timeToMinutes(scheduleData?.duration),
      durationType: scheduleData?.duration_type,
      tenant: scheduleData?.tenant,
      type: scheduleData?.type,
    });
  } else
    return ({
      id: '',
      dateStart: '',
      dateEnd: '',
      timeSlots: [],
      weekdays: [],
      duration: null,
      durationType: null,
      tenant: null,
      type: null,
    });
};

const EditAvailabilityModal = ({ open, setClose, date, refresh }: EditAvailabilityModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { slug } = useAuth();
  const tenantId = useSelector((state: RootState) => state.meetings.tenantId);
  const canAddInterval = useSelector(
    (state: RootState) => state.meetings.canAddInterval,
  );
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const { createToastNotification } = useToastNotificationContext();
  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const [scheduleData, setScheduleData] = useState<ScheduleDataObject>({
    id: '',
    dateStart: '',
    dateEnd: '',
    timeSlots: [],
    duration: null,
    durationType: null,
    tenant: null,
    type: null,
  });

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const { data } = useSWR((companySlug && date) ? `schedule/availability/tenant/${companySlug}/calendar/${formatDate(date)}` : '', { revalidateOnFocus: false });

  useEffect(() => {
    setScheduleData(transformDataFromAPI(data));
    dispatch(setCanAddInterval(true));
  }, [data, dispatch]);

  //add new item to timeslot intervals
  const addIntervalhandler = () => {
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [
      ...prevData.timeSlots,
      {
        from: '',
        to: '',
      },
    ] }));
  };

  //remove item from timeslot intervals 
  const removeIntervalhandler = (index: number) => {
    const newIntervals: IntervalsObject[] = [...scheduleData.timeSlots];
    if (newIntervals[index] && newIntervals[index]?.id) {
      newIntervals[index].is_delete = true;
    } else {
      newIntervals.splice(index, 1);
    }
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [...newIntervals] }));
  };

  //set timeslot interval base on index
  const setIntervalValues = (index: number, key: string, value: string) => {
    const newIntervals: IntervalsObject[] = [...scheduleData.timeSlots];
    if (index >= 0 && newIntervals.length > index) {
      if (key === 'from')
        newIntervals[index].from = value;
      if (key === 'to')
        newIntervals[index].to = value;
    }
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [...newIntervals] }));
  };

  //clear all intervals 
  const clearDuration = () => {
    const clearedIntervals: IntervalsObject[] = scheduleData?.timeSlots.filter(time => time.id).map(time => {time.is_delete = true; return time;});
    setScheduleData((prevData) => ({ ...prevData, timeSlots: clearedIntervals }));
  };

  //check values before sending to server
  const isValuesValid = (): boolean => {
    if (!scheduleData.duration || scheduleData.duration < 10) {
      createToastNotification(intl.formatMessage({ defaultMessage: 'Duration cannot be empty or below 10 mins' }), 'error');
      return false;
    } 
    if (scheduleData.timeSlots.find(({ from, to, is_delete }) => !from && !to && !is_delete)) {
      createToastNotification(intl.formatMessage({ defaultMessage: 'Time slot cannot have empty fields' }), 'error');
      return false;
    }
    return true;
  };

  const scheduleAvailabilityOnSpecificDay = async () => {
    if ((!scheduleData?.id || scheduleData.type === 10) && isValuesValid()) {
      try {
        setRequestLoading(true);
        const res = await axiosInstance().post('schedule/availability', {
          date_start: new Date(date + ' UTC').toISOString(),
          date_end: new Date(date + ' UTC').toISOString(),
          duration: scheduleData.duration,
          tenant: tenantId,
          type: '16',
          duration_type: '1',
          // filter all time_slot with 'is_delete' and remove all id after. 
          time_slots: convertTimeRangeHourFormat(scheduleData.timeSlots?.filter( time => !time.is_delete)?.map( time => {
            return {
              from:time.from,
              to: time.to,
            };
          })), // 24 hours
        });
        if (res) {
          createToastNotification(intl.formatMessage({ defaultMessage: 'Availability Schedule Saved.' }), 'success');
          refresh();
        }
      } catch (err) {
        createToastNotification(intl.formatMessage({ defaultMessage: 'Something went wrong.' }), 'error');
      }
      setRequestLoading(false);
    } else if (scheduleData?.id && isValuesValid()) {
      try {
        const res = await axiosInstance().patch(`schedule/availability/${scheduleData?.id}`, {
          date_start: new Date(date + ' UTC').toISOString(),
          date_end: new Date(date + ' UTC').toISOString(),
          duration: scheduleData.duration,
          tenant: scheduleData.tenant,
          duration_type: scheduleData.durationType,
          time_slots: convertTimeRangeHourFormat(scheduleData.timeSlots),
        });
        if (res) {
          createToastNotification(intl.formatMessage({ defaultMessage: 'Availability Schedule Saved.' }), 'success');
          refresh();
        }
      } catch (err) {
        createToastNotification(intl.formatMessage({ defaultMessage: 'Something went wrong.' }), 'error');
      }
      setRequestLoading(false);
    }
  };

  const changeDuration = (newDuration: number | null) => {
    setScheduleData((prevData) => ({ ...prevData, duration: newDuration }));
    const clearedIntervals = scheduleData?.timeSlots.map(({ id, is_delete }) => ({
      from: '',
      to: '',
      id: id,
      is_delete: is_delete,
    }));
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [...clearedIntervals] }));
  };


  return (
    <Modal
      open={open}
      onClose={setClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack sx={styles.container} gap={2}>
        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' gap={1} justifyContent='center' flexGrow={1}>
            <Typography sx={styles.headerStyle}>
              <FormattedMessage defaultMessage='Edit availability' />
            </Typography>
          </Stack>
          <IconButton sx={{ padding: '0px' }} onClick={setClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ width: 'calc(100% + 80px)', marginLeft: '-40px' }} />
        <Stack 
          direction = 'row' 
          flexGrow = {1}
          alignItems='center' 
          justifyContent='space-between' 
        >
          <Typography sx={styles.bodyTextStyle}>
            <FormattedMessage defaultMessage='Duration' />
          </Typography>
          <Button sx={{
            ...styles.buttonStyle,
            ...styles.buttonTextOnly,
          }}
          onClick={clearDuration}>
            <FormattedMessage defaultMessage='Clear' />
          </Button>
        </Stack>
        <Stack gap={1}>
          <Typography sx={{
            ...styles.bodyTextStyle,
            color: '#6A6A6A',
          }}>
            <FormattedMessage defaultMessage='Display available time slots and duration for users' />
          </Typography>
          <SelectDurationDropdown 
            selected={scheduleData?.duration} 
            setSelected={changeDuration} 
            disabledFlag={false}
          />
        </Stack>
        <Divider sx={{ width: 'calc(100% + 80px)', marginLeft: '-40px' }} />
        <Stack>
          {scheduleData?.timeSlots?.length === 0 && 
            <Stack alignItems='center' sx={styles.unavailable}>
              <Typography sx={{
                ...styles.bodyTextStyle,
                color:'#878787',
              }}>
                <FormattedMessage defaultMessage='Unavailable' />
              </Typography>
            </Stack>
          }
          {
            scheduleData?.timeSlots?.length > 0 && 
            <Stack direction='row' justifyContent='space-around'>
              <Stack flexBasis='45%'>
                <Typography sx={styles.bodyTextStyle}>
                  <FormattedMessage defaultMessage='From' />
                </Typography>
              </Stack>
              <Stack flexBasis='55%'>
                <Typography sx={styles.bodyTextStyle}>
                  <FormattedMessage defaultMessage='To' />
                </Typography>
              </Stack>
            </Stack>
          }
          <Stack gap={1} sx={styles.intervalContaner}>
            {scheduleData?.timeSlots && scheduleData?.timeSlots?.map((interval, key) => (
              <IntervalComponent 
                intervals={scheduleData?.timeSlots}
                from={interval.from}
                to={interval.to}
                isDeleted={interval.is_delete}
                setFrom={(e) => setIntervalValues(key, 'from', e)}
                setTo={(e)=> setIntervalValues(key, 'to', e)}
                index={key}
                remove={removeIntervalhandler}
                key={key}
                duration={scheduleData.duration ?? 0}/>
            ))}
          </Stack>
          <Button sx={{
            ...styles.buttonStyle,
            ...styles.buttonTextAdd,
          }}
          disabled={!scheduleData.duration || !canAddInterval}
          startIcon={<AddIcon sx={{ color: canAddInterval ? '#00CECF' : '#878787' }}/>}
          onClick={addIntervalhandler}>
            <FormattedMessage defaultMessage='New interval' />
          </Button>
        </Stack>
        
        <Stack direction='row' gap={1}>
          <Button sx={{
            ...styles.buttonStyleCancel,
            width: '100%',
          }}
          onClick={setClose}>
            <FormattedMessage defaultMessage='Cancel' />
          </Button>
          <Button sx={{
            ...styles.buttonStyle,
            width: '100%',
          }}
          onClick={scheduleAvailabilityOnSpecificDay}>
            {requestLoading && <CircularProgress size={20} sx={{ color: '#ffffff' }}/>}
            {!requestLoading && <FormattedMessage defaultMessage='Apply' />}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default EditAvailabilityModal;